import { ChangeDetectionStrategy, Component, computed, forwardRef, input, model, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Popover } from 'primeng/popover';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonDirective } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDragPlaceholder, CdkDropList } from '@angular/cdk/drag-drop';
export interface OrderedSelectItem {
  label?: string;
  value: unknown;
  rank: number;
  selected: boolean;
}

@Component({
  selector: 'well-reorderable-multi-select-cva',
  host: { class: 'irian-custom-cva' },
  standalone: true,
  imports: [
    CommonModule,
    Popover,
    CheckboxModule,
    FormsModule,
    ButtonDirective,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder,
  ],
  templateUrl: './reorderable-multi-select-cva.component.html',
  styleUrl: './reorderable-multi-select-cva.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ReorderableMultiSelectCvaComponent),
      multi: true,
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReorderableMultiSelectCvaComponent implements ControlValueAccessor {
  options = model.required<OrderedSelectItem[]>();
  displayOptions = computed(() => {
    const options = this.options();
    const searchQuery = this.searchQuery().trim().toLowerCase();

    return options
      .filter((option) => {
        const standardizedLabel = option.label?.trim().toLowerCase();
        return standardizedLabel?.includes(searchQuery);
      });
  });

  placeholder = input<string>('');
  searchQuery = signal<string>('');

  selectedLabels = computed(() => {
    const selectedOptions = this.options().filter((option) => option.selected);

    return selectedOptions.filter((item) => item.label && item.selected).map((item) => item.label);
  });
  areAllOptionsSelected = computed(() => {
    const selectedLabels = this.selectedLabels();
    const filteredLabels = this.displayOptions().map((option) => option.label);

    return !filteredLabels.some((label) => selectedLabels.indexOf(label) === -1);
  });

  focused = signal<boolean>(false);
  open = signal<boolean>(false);

  ngOnInit() {
    for(let i = 0; i < 20; i++) {
      this.options().push({
        label: "test" + i,
        value: i,
        rank: i,
        selected: false
      })
    }
  }

  private onTouched: () => void = () => {
    return;
  };
  private onChanged: (value: OrderedSelectItem[]) => void = (value: OrderedSelectItem[]) => {
    return;
  };

  registerOnChange(fn: (value: OrderedSelectItem[]) => void) {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  // value of selectedOptions will be all provided options, with rank and selected
  // if you need only the selected items, filter based on selected
  writeValue(value: OrderedSelectItem[]) {
    if (value && value.length > 0) {
      this.options.set(value);
    }
  }

  toggleOpen() {
    const open = this.open();
    this.open.set(!open);
  }

  selectOrDeselectAll($event: boolean) {
    if ($event) {
      this.selectAll();
    } else {
      this.deselectAll();
    }
  }

  private selectAll() {
    const options = this.displayOptions().map((option) => ({
      ...option,
      selected: true,
    }));
    this.writeValue(options);
    this.onChanged(options);
    this.onTouched();
  }

  private deselectAll() {
    const deselected = this.options().map((option, index) => ({
      ...option,
      selected: false,
    }));
    this.writeValue(deselected);
    this.onChanged(deselected);
    this.onTouched();
  }

  protected selectItem(option: OrderedSelectItem) {
    this.options.update((prev) => {
      const index = prev.findIndex((item) => item.value === option.value);
      if (index === -1) {
        prev.push({
          label: option.label,
          value: option.value,
          rank: option.rank,
          selected: true,
        });
      } else {
        prev[index] = {
          ...prev[index],
          selected: !option.selected,
        };
      }

      return [...prev];
    });

    this.onChanged(this.options());
    this.onTouched();
  }

  protected dropOption<T>($event: CdkDragDrop<T, unknown>) {
    const prevIndex = $event.previousIndex;
    const newIndex = $event.currentIndex;
    const displayList = this.displayOptions();
    const options = this.options();

    if (prevIndex === newIndex) {
      return;
    }

    const prevOriginalIndex = options.findIndex((option) => option.value === displayList[prevIndex].value);
    const newOriginalIndex = options.findIndex((option) => option.value === displayList[newIndex].value);

    this.options.set(this.updateOptions(options, prevOriginalIndex, newOriginalIndex));
    this.updateSelectedOptions();
  }

  private updateOptions(options: OrderedSelectItem[], prevIndex: number, newIndex: number) {
    let newArray: OrderedSelectItem[];
    const excludedOption = options[prevIndex].value;

    if (prevIndex > newIndex) {
      // case: moved up
      const upperHalf = options.slice(0, newIndex);
      const lowerHalfExcludingDropped = options.slice(newIndex).filter((option) => option.value !== excludedOption);

      newArray = [...upperHalf, options[prevIndex], ...lowerHalfExcludingDropped];
    } else {
      // case: moved down
      const lowerHalf = options.slice(newIndex + 1);
      const upperHalfExcludingDropped = options
        .slice(0, newIndex + 1)
        .filter((option) => option.value !== excludedOption);

      newArray = [...upperHalfExcludingDropped, options[prevIndex], ...lowerHalf];
    }
    return newArray;
  }

  private updateSelectedOptions() {
    const updatedSelectedOptions: OrderedSelectItem[] = [];
    const options = this.options();

    options.forEach((option, index) => {
      updatedSelectedOptions.push({
        ...option,
        rank: index,
      });
    });

    this.writeValue(updatedSelectedOptions);
    this.onChanged(updatedSelectedOptions);
    this.onTouched();
  }
}
